// Shared thumbs-up/down feedback control for report-style modals (Condition
// report Timeline tab, per-item report modal). Mirrors the inline pattern in
// SuggestionCard.tsx / PtSheetScanModal.tsx (thumbs-up submits immediately,
// thumbs-down opens an optional checklist + free-text reason) but factored
// out since this is the third+ near-identical copy.

import { useState } from "react";
import { ApiError } from "../lib/api.js";
import { ThumbUpIcon, ThumbDownIcon } from "./icons.js";
import { ClearableTextarea } from "./ClearableInput.js";

export interface FeedbackChecklistItem {
  id: string;
  label: string;
}

export interface FeedbackThumbsSubmitOpts {
  reasonText?: string;
  flaggedIds?: string[];
}

interface Props {
  /** e.g. "Does this timeline look right?" */
  prompt: string;
  /** Optional multi-select list shown on thumbs-down (e.g. which symptom/row looks wrong). */
  checklist?: FeedbackChecklistItem[];
  checklistLabel?: string;
  placeholder?: string;
  onSubmit: (rating: "up" | "down", opts: FeedbackThumbsSubmitOpts) => Promise<void>;
}

export function FeedbackThumbs({ prompt, checklist, checklistLabel, placeholder, onSubmit }: Props) {
  const [rating, setRating] = useState<"up" | "down" | null>(null);
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleThumb(r: "up" | "down") {
    if (r === "up") {
      setBusy(true);
      setError(null);
      try {
        await onSubmit("up", {});
        setRating("up");
        setOpen(false);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Couldn't save feedback.");
      } finally {
        setBusy(false);
      }
      return;
    }
    setRating("down");
    setOpen(true);
    setChecked(new Set());
    setText("");
  }

  async function handleSubmitDown() {
    setBusy(true);
    setError(null);
    try {
      await onSubmit("down", {
        reasonText: text.trim() || undefined,
        flaggedIds: Array.from(checked),
      });
      setOpen(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save feedback.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border-t border-gray-100 dark:border-gray-800 pt-2">
      {!open && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{prompt}</span>
          <button
            onClick={() => void handleThumb("up")}
            disabled={busy}
            title="Thumbs up"
            aria-label="Feedback: helpful"
            aria-pressed={rating === "up"}
            className={
              "rounded p-1 transition-colors disabled:opacity-40 " +
              (rating === "up"
                ? "text-green-600 dark:text-green-400"
                : "text-gray-300 hover:text-green-600 dark:text-gray-600 dark:hover:text-green-400")
            }
          >
            <ThumbUpIcon size={15} />
          </button>
          <button
            onClick={() => void handleThumb("down")}
            disabled={busy}
            title="Thumbs down — report an issue"
            aria-label="Feedback: not helpful"
            aria-pressed={rating === "down"}
            className={
              "rounded p-1 transition-colors disabled:opacity-40 " +
              (rating === "down"
                ? "text-red-600 dark:text-red-400"
                : "text-gray-300 hover:text-red-600 dark:text-gray-600 dark:hover:text-red-400")
            }
          >
            <ThumbDownIcon size={15} />
          </button>
          {rating === "up" && <span className="text-xs text-gray-400" aria-live="polite">Thanks!</span>}
          {rating === "down" && (
            <button onClick={() => setOpen(true)} className="text-xs text-gray-400 hover:underline">
              Edit reason
            </button>
          )}
          {error && <span className="text-xs text-red-600 dark:text-red-400">{error}</span>}
        </div>
      )}
      {open && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">What's wrong?</span>
            <button
              onClick={() => setOpen(false)}
              className="ml-auto text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label="Close feedback form"
            >
              ✕
            </button>
          </div>
          {checklist && checklist.length > 0 && (
            <div className="space-y-1">
              {checklistLabel && (
                <span className="text-xs text-gray-500 dark:text-gray-400">{checklistLabel}</span>
              )}
              {checklist.map((item) => (
                <label
                  key={item.id}
                  className="flex cursor-pointer items-center gap-2 text-xs text-gray-800 dark:text-gray-200"
                >
                  <input
                    type="checkbox"
                    checked={checked.has(item.id)}
                    onChange={() =>
                      setChecked((prev) => {
                        const next = new Set(prev);
                        if (next.has(item.id)) next.delete(item.id);
                        else next.add(item.id);
                        return next;
                      })
                    }
                    className="accent-red-500"
                  />
                  {item.label}
                </label>
              ))}
            </div>
          )}
          <ClearableTextarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={placeholder ?? "What looks wrong? (optional)"}
            rows={2}
            className="w-full resize-none rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-xs text-gray-800 dark:text-gray-200 outline-none focus:ring-1 focus:ring-red-400"
          />
          {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
          <div className="flex gap-2">
            <button
              onClick={() => void handleSubmitDown()}
              disabled={busy}
              className="rounded bg-red-600 px-3 py-1 text-xs text-white hover:bg-red-700 disabled:opacity-50"
            >
              {busy ? "Sending…" : "Submit"}
            </button>
            <button
              onClick={() => setOpen(false)}
              className="rounded border border-gray-200 dark:border-gray-700 px-3 py-1 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
